import { IonAvatar } from "@ionic/react";
import { useIonRouter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { dayStart, format } from "@formkit/tempo";
import EmptyAvatar from "@/assets/empty_avatar.svg";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import type { TypedConversation } from "@/lib/query/conversation/conversation.types";
import { Routes } from "@/lib/router";
import { cx } from "@/lib/style/cva.config";

interface ConversationListItemProps {
  conversation: TypedConversation;
}

function isToday(date: Date): boolean {
  const today = dayStart(new Date());
  const target = dayStart(date);
  return today.getTime() === target.getTime();
}

function isYesterday(date: Date): boolean {
  const yesterday = dayStart(new Date());
  yesterday.setDate(yesterday.getDate() - 1);
  const target = dayStart(date);
  return yesterday.getTime() === target.getTime();
}

export function ConversationListItem({ conversation }: ConversationListItemProps) {
  const { t } = useTranslation();
  const haptics = usePreferredHaptics();
  const router = useIonRouter();

  // Use displayTitle from the conversation (computed by backend for the requesting user)
  const displayName = conversation.displayTitle || t("routes:conversations.unknown-participant");

  // Get avatar from the first CONTACT participant
  const contactParticipant = conversation.participants?.find((p) => p.side === "CONTACT");
  const avatarUrl =
    contactParticipant?.avatarUrl || contactParticipant?.messageContact?.avatarUrl || undefined;

  // Format timestamp
  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) {
      return "";
    }
    const date = new Date(timestamp);
    if (isToday(date)) {
      return format(date, "HH:mm");
    }
    if (isYesterday(date)) {
      return t("common:misc.yesterday");
    }
    return format(date, "D MMM", "nl");
  };

  const timestamp = formatTimestamp(conversation.lastMessageAt);
  const unreadCount = conversation.unreadCount || 0;

  const handleClick = () => {
    haptics.impact();
    router.push(Routes.Conversation({ id: conversation.id }));
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full items-start gap-3 rounded-lg px-2 py-3 text-left transition-colors active:bg-gray-50"
    >
      {/* Avatar */}
      <IonAvatar className="size-[51px] shrink-0">
        <img
          src={avatarUrl || EmptyAvatar}
          alt={displayName}
          className="size-full rounded-full object-cover"
        />
      </IonAvatar>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {/* Name and timestamp row */}
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center justify-between gap-2">
            <span
              className={cx(
                "truncate text-base leading-[1.5] tracking-[0.08px]",
                unreadCount > 0
                  ? "font-semibold text-txt-primary"
                  : "font-semibold text-txt-primary",
              )}
            >
              {displayName}
            </span>
            <span className="shrink-0 text-sm font-medium leading-[1.4] text-txt-tertiary">
              {timestamp}
            </span>
          </div>

          {/* Message preview */}
          <span
            className={cx(
              "truncate text-sm font-medium leading-[1.4]",
              unreadCount > 0 ? "text-txt-primary" : "text-txt-tertiary",
            )}
          >
            {conversation.lastMessagePreview || ""}
          </span>
        </div>

        {/* Tags row - placeholder for future tags */}
        {/* Tags will be added later as per design */}
      </div>
    </button>
  );
}
